import { useEffect, useState } from 'react'
import { Row, Col, Card, Statistic, Spin, Table, Progress, Tag } from 'antd'
import {
    MessageOutlined,
    TeamOutlined,
    SendOutlined,
    InboxOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    UserOutlined,
    RiseOutlined
} from '@ant-design/icons'
import api from '../services/api'

interface Stats {
    total_messages: number
    messages_today: number
    total_contacts: number
    new_contacts_today: number
    messages_sent: number
    messages_received: number
    total_accounts: number
    active_accounts: number
    total_operators: number
    online_operators: number
}

interface TaskStats {
    total_tasks: number
    completed_tasks: number
    pending_tasks: number
    tasks_by_assignee: { name: string; count: number; completed: number }[]
    tasks_by_creator: { name: string; count: number }[]
}

interface OperatorStat {
    operator_id: number
    operator_name: string
    messages_sent: number
    active_chats: number
    is_online: boolean
}

export default function Statistics() {
    const [loading, setLoading] = useState(true)
    const [stats, setStats] = useState<Stats | null>(null)
    const [taskStats, setTaskStats] = useState<TaskStats | null>(null)
    const [operatorStats, setOperatorStats] = useState<OperatorStat[]>([])

    useEffect(() => {
        fetchStats()
    }, [])

    const fetchStats = async () => {
        setLoading(true)
        try {
            // Fetch dashboard stats
            const dashboardRes = await api.get('/stats/dashboard')
            setStats(dashboardRes.data)

            // Fetch operator stats
            try {
                const operatorRes = await api.get('/stats/operators')
                setOperatorStats(operatorRes.data.operators || [])
            } catch { }

            // Fetch task stats
            try {
                const tasksRes = await api.get('/tasks/stats')
                setTaskStats(tasksRes.data)
            } catch { }

        } catch (error) {
            console.error('Error fetching stats:', error)
        } finally {
            setLoading(false)
        }
    }

    // Stat Card Component
    const StatCard = ({
        title,
        value,
        icon,
        color,
        suffix,
        subtitle
    }: {
        title: string
        value: number | string
        icon: React.ReactNode
        color: string
        suffix?: string
        subtitle?: string
    }) => (
        <Card
            style={{
                background: 'var(--bg-surface)',
                borderRadius: 16,
                border: '1px solid var(--border)',
                overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
        >
            <div style={{
                background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
                padding: 20,
                borderBottom: `2px solid ${color}30`
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: `${color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        color: color
                    }}>
                        {icon}
                    </div>
                    <div>
                        <div style={{
                            fontSize: 13,
                            color: 'var(--text-muted)',
                            marginBottom: 2
                        }}>
                            {title}
                        </div>
                        <div style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                            lineHeight: 1
                        }}>
                            {value}{suffix && <span style={{ fontSize: 16, marginLeft: 4 }}>{suffix}</span>}
                        </div>
                        {subtitle && (
                            <div style={{ fontSize: 12, color: color, marginTop: 4 }}>
                                {subtitle}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    )

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" />
            </div>
        )
    }

    const operatorColumns = [
        {
            title: 'Оператор',
            dataIndex: 'operator_name',
            key: 'operator_name',
            render: (name: string, record: OperatorStat) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: record.is_online ? '#10b981' : '#6b7280'
                    }} />
                    {name}
                </div>
            )
        },
        {
            title: 'Сообщений сегодня',
            dataIndex: 'messages_sent',
            key: 'messages_sent',
            render: (val: number) => <Tag color="blue">{val}</Tag>
        },
        {
            title: 'Активных чатов',
            dataIndex: 'active_chats',
            key: 'active_chats',
            render: (val: number) => <Tag color="green">{val}</Tag>
        },
        {
            title: 'Статус',
            dataIndex: 'is_online',
            key: 'is_online',
            render: (online: boolean) => online ?
                <Tag color="success">Онлайн</Tag> :
                <Tag color="default">Офлайн</Tag>
        }
    ]

    return (
        <div style={{ padding: 24 }}>
            {/* Header */}
            <div style={{ marginBottom: 32 }}>
                <h1 style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                }}>
                    <RiseOutlined style={{ color: '#667eea' }} />
                    Статистика
                </h1>
                <p style={{
                    color: 'var(--text-muted)',
                    margin: '8px 0 0',
                    fontSize: 15
                }}>
                    Обзор активности и показателей системы
                </p>
            </div>

            {/* Messages Stats */}
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: 16 }}>📨 Сообщения</h3>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Всего сообщений"
                            value={stats?.total_messages || 0}
                            icon={<MessageOutlined />}
                            color="#667eea"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Сообщений сегодня"
                            value={stats?.messages_today || 0}
                            icon={<SendOutlined />}
                            color="#10b981"
                            subtitle="за текущий день"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Отправлено"
                            value={stats?.messages_sent || stats?.messages_today || 0}
                            icon={<SendOutlined />}
                            color="#3b82f6"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Получено"
                            value={stats?.messages_received || 0}
                            icon={<InboxOutlined />}
                            color="#f59e0b"
                        />
                    </Col>
                </Row>
            </div>

            {/* Contacts Stats */}
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: 16 }}>👥 Контакты</h3>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Всего контактов"
                            value={stats?.total_contacts || 0}
                            icon={<TeamOutlined />}
                            color="#ec4899"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Новых сегодня"
                            value={stats?.new_contacts_today || 0}
                            icon={<UserOutlined />}
                            color="#8b5cf6"
                            subtitle="новые контакты"
                        />
                    </Col>
                </Row>
            </div>

            {/* Tasks Stats */}
            {taskStats && (
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ color: 'var(--text-primary)', marginBottom: 16 }}>✅ Задачи</h3>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} lg={6}>
                            <StatCard
                                title="Всего задач"
                                value={taskStats.total_tasks}
                                icon={<ClockCircleOutlined />}
                                color="#6366f1"
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatCard
                                title="Выполнено"
                                value={taskStats.completed_tasks}
                                icon={<CheckCircleOutlined />}
                                color="#10b981"
                                subtitle={`${Math.round((taskStats.completed_tasks / (taskStats.total_tasks || 1)) * 100)}% от всех`}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <StatCard
                                title="В работе"
                                value={taskStats.pending_tasks}
                                icon={<ClockCircleOutlined />}
                                color="#f59e0b"
                            />
                        </Col>
                    </Row>

                    {/* Tasks by assignee */}
                    {taskStats.tasks_by_assignee && taskStats.tasks_by_assignee.length > 0 && (
                        <Card
                            title="Задачи по исполнителям"
                            style={{
                                marginTop: 16,
                                background: 'var(--bg-surface)',
                                borderRadius: 12,
                                border: '1px solid var(--border)'
                            }}
                        >
                            {taskStats.tasks_by_assignee.map((item, idx) => (
                                <div key={idx} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span>{item.name}</span>
                                        <span>{item.completed} / {item.count}</span>
                                    </div>
                                    <Progress
                                        percent={Math.round((item.completed / (item.count || 1)) * 100)}
                                        strokeColor="#10b981"
                                        trailColor="var(--border)"
                                    />
                                </div>
                            ))}
                        </Card>
                    )}
                </div>
            )}

            {/* Operators Stats */}
            {operatorStats.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ color: 'var(--text-primary)', marginBottom: 16 }}>👤 Операторы</h3>
                    <Card
                        style={{
                            background: 'var(--bg-surface)',
                            borderRadius: 12,
                            border: '1px solid var(--border)'
                        }}
                    >
                        <Table
                            dataSource={operatorStats}
                            columns={operatorColumns}
                            rowKey="operator_id"
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </div>
            )}

            {/* Accounts Quick Stats */}
            <div>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: 16 }}>📱 Аккаунты</h3>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Всего аккаунтов"
                            value={stats?.total_accounts || 0}
                            icon={<UserOutlined />}
                            color="#0ea5e9"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <StatCard
                            title="Активных"
                            value={stats?.active_accounts || 0}
                            icon={<CheckCircleOutlined />}
                            color="#10b981"
                        />
                    </Col>
                </Row>
            </div>
        </div>
    )
}
